import { Drawer as Base } from "@base-ui/react/drawer";
import { IconX } from "@tabler/icons-react";
import type { ReactNode } from "react";

/**
 * A panel that slides in from the side, on Base UI's Drawer: focus moves in, Escape closes it, focus goes
 * back to what opened it, and on touch screens it can be swiped away. Modal by default (a backdrop, focus
 * kept inside, a click outside closes it); `modal={false}` leaves the page usable beside it (a chat dock).
 */
export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Keep the title for screen readers only, when the content has its own heading. */
  hideTitle?: boolean;
  /** Where it slides in from; "bottom" is a sheet (phones). */
  side?: "right" | "left" | "bottom";
  modal?: boolean;
  /** Widest the panel gets, in pixels; default 440 (a bottom sheet takes the full width). */
  width?: number;
  className?: string;
  children: ReactNode;
}

export function Drawer({ open, onOpenChange, title, hideTitle = false, side = "right", modal = true, width = 440, className, children }: DrawerProps) {
  return (
    <Base.Root open={open} onOpenChange={(o) => onOpenChange(o)} swipeDirection={side === "bottom" ? "down" : side} modal={modal} disablePointerDismissal={!modal}>
      <Base.Portal>
        {modal && <Base.Backdrop className="drawer-backdrop" />}
        <Base.Viewport className={`drawer-viewport ${side}`}>
          <Base.Popup className={`drawer ${side}${className ? ` ${className}` : ""}`} style={side === "bottom" ? undefined : { width: `min(${width}px, calc(100vw - 32px))` }}>
            <Base.Content className="drawer-content">
              <div className={hideTitle ? "drawer-head bare" : "drawer-head"}>
                <Base.Title className={hideTitle ? "sr-only" : undefined} render={<h2 />}>{title}</Base.Title>
                <Base.Close className="ghost icon drawer-close" aria-label="Close"><IconX size={16} /></Base.Close>
              </div>
              {children}
            </Base.Content>
          </Base.Popup>
        </Base.Viewport>
      </Base.Portal>
    </Base.Root>
  );
}
